import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import { leaveServices } from '../../services/leaveServices';
import { holidayServices } from '../../services/holidayServices';
import { useSnackbar } from '../../utils/useSnackbar';
import HolidayDatePicker from '../common/HolidayDatePicker';
import '../../css/Dialog.css';
import '../../css/Leave.css';
import { LEAVE_TYPE, isHalfDay } from '../../constants/leaveType';

const toLocalDateTime = (dateStr) => {
  if (!dateStr) return null;
  return `${dateStr}T00:00:00`;
};

const fromLocalDateTime = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

const toDateStr = (d) => {
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMinStartDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 2);
  return toDateStr(d);
};

const getMaxEndDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 6);
  return toDateStr(d);
};

const calcLeave = (startStr, endStr, leaveType = LEAVE_TYPE.FULL_DAY, holidays = []) => {
  if (!startStr) return null;

  const holidaySet = new Set(
    (holidays || []).map((h) => h?.holidayDate?.split('T')[0]).filter(Boolean)
  );

  if (isHalfDay(leaveType)) {
    if (holidaySet.has(startStr)) {
      return { days: 0, isSandwich: false };
    }
    return { days: 0.5, isSandwich: false };
  }

  if (!endStr || endStr < startStr) return null;

  if (startStr === endStr && holidaySet.has(startStr)) {
    return { days: 0, isSandwich: false };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const noticeDays = Math.round((start - today) / (1000 * 60 * 60 * 24));
  const sandwichRuleApplies = noticeDays < 30;

  let days = 0;
  let hasWeekend = false;
  const cur = new Date(start);
  while (cur <= end) {
    const dateISO = toDateStr(cur);
    const isHoliday = holidaySet.has(dateISO);
    const dow = cur.getDay();
    const isWeekend = dow === 0 || dow === 6;
    if (isWeekend) hasWeekend = true;

    // Exclude company holidays
    if (!isHoliday) {
      if (sandwichRuleApplies || !isWeekend) days++;
    }
    cur.setDate(cur.getDate() + 1);
  }

  const isSandwich = sandwichRuleApplies && hasWeekend && days > 0;
  return { days, isSandwich };
};

const badgeLabel = (info) => {
  if (!info || info.days === 0) return '0 days leave';
  const dayStr = info.days === 0.5 ? 'Half day' : `${info.days} day${info.days !== 1 ? 's' : ''}`;
  return info.isSandwich ? `${dayStr} leave · sandwich leave` : `${dayStr} leave`;
};

const EditLeaveDialog = ({ open, onClose, leave, onSuccess }) => {
  const [leaveType, setLeaveType] = useState(LEAVE_TYPE.FULL_DAY);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [appliedLeaves, setAppliedLeaves] = useState([]);

  const minStartDate = useMemo(() => getMinStartDate(), []);
  const maxEndDate = useMemo(() => getMaxEndDate(), []);

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  useEffect(() => {
    if (open) {
      holidayServices.getHolidaysBetween(minStartDate, maxEndDate)
        .then((res) => {
          setHolidays(Array.isArray(res) ? res : res?.content || []);
        })
        .catch((err) => {
          console.error('Failed to load holidays for edit leave dialog:', err);
        });

      if (leave?.userId) {
        leaveServices.getLeavesByUserId(leave.userId, 'ALL', minStartDate, maxEndDate, 0, 100)
          .then((res) => {
            const list = (res?.content || []).filter((lv) => lv.id !== leave.id);
            setAppliedLeaves(list);
          })
          .catch((err) => {
            console.error('Failed to load user leaves for edit dialog:', err);
          });
      }
    }
  }, [open, leave, minStartDate, maxEndDate]);

  const halfDay = isHalfDay(leaveType);
  const effectiveEndDate = halfDay ? startDate : endDate;

  const leaveInfo = useMemo(
    () => calcLeave(startDate, effectiveEndDate, leaveType, holidays),
    [startDate, effectiveEndDate, leaveType, holidays]
  );

  const holidaysInRange = useMemo(() => {
    if (!startDate || !holidays?.length) return [];
    const end = halfDay ? startDate : (endDate || startDate);
    return holidays.filter((h) => {
      const d = h.holidayDate?.split('T')[0];
      return d && d >= startDate && d <= end;
    });
  }, [startDate, endDate, halfDay, holidays]);

  useEffect(() => {
    if (leave && open) {
      setLeaveType(leave.leaveType || LEAVE_TYPE.FULL_DAY);
      setStartDate(fromLocalDateTime(leave.startDate));
      setEndDate(fromLocalDateTime(leave.endDate));
      setReason(leave.reason || '');
    }
  }, [leave, open]);

  const handleLeaveTypeChange = (_, newType) => {
    if (!newType) return;
    setLeaveType(newType);
    if (isHalfDay(newType)) setEndDate('');
  };

  const validate = () => {
    if (!startDate) {
      showSnackbar('Please select a start date.', 'error');
      return false;
    }
    if (!halfDay) {
      if (!endDate) {
        showSnackbar('Please select an end date.', 'error');
        return false;
      }
      if (startDate && endDate && endDate < startDate) {
        showSnackbar('End date cannot be before start date.', 'error');
        return false;
      }
    }

    const holidayMap = {};
    holidays.forEach((h) => {
      if (h?.holidayDate) holidayMap[h.holidayDate.split('T')[0]] = h;
    });

    // 1. Single day leave on holiday
    if (startDate && (halfDay || startDate === (effectiveEndDate || startDate))) {
      if (holidayMap[startDate]) {
        showSnackbar(`Selected date is a company holiday (${holidayMap[startDate].name}). Leave cannot be applied on a holiday.`, 'error');
        return false;
      }
    } else if (leaveInfo && leaveInfo.days === 0) {
      // 2. Multi-day range with only holidays / off-days
      showSnackbar('Selected date range contains only holidays or non-working days. No leave required.', 'error');
      return false;
    }

    if (!reason.trim()) {
      showSnackbar('Please enter a reason for your leave.', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      id: leave.id,
      userId: leave.userId,
      teamId: leave.teamId,
      startDate: toLocalDateTime(startDate),
      endDate: toLocalDateTime(effectiveEndDate),
      reason: reason.trim(),
      leaveType,
    };

    try {
      setSubmitting(true);
      await leaveServices.editLeaveRequest(payload);
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to update leave request. Please try again.';
      showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!submitting ? onClose : undefined}
      slotProps={{ paper: { className: 'modern-dialog' } }}
      maxWidth="sm"
      fullWidth
    >
      {SnackbarComponent}
      <DialogTitle className="modern-dialog-title">
        <span className="modern-dialog-title-text">Edit Leave Request</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={submitting}
          className="modern-dialog-close-btn"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit} noValidate>
        <DialogContent className="modern-dialog-content">

          {/* Leave Type */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75 }}>
              Leave Type
            </Typography>
            <Box className="leave-type-row">
              <ToggleButtonGroup
                value={leaveType}
                exclusive
                onChange={handleLeaveTypeChange}
                size="small"
                sx={{
                  boxShadow: '0 0 0 1px rgba(4, 88, 100, 0.28)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRight: '1px solid rgba(4, 88, 100, 0.15)',
                    borderRadius: '0 !important',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    flex: 1,
                    minWidth: 96,
                    gap: '4px',
                    color: '#64748b',
                    padding: '5px 0',
                    transition: 'background-color 0.15s, color 0.15s',
                    '&:last-child': { borderRight: 'none' },
                    '&.Mui-selected': {
                      backgroundColor: 'var(--primary)',
                      color: '#fff',
                      borderRight: '1px solid rgba(4, 88, 100, 0.25)',
                      '&:last-child': { borderRight: 'none' },
                      '&:hover': { backgroundColor: 'var(--primary)', opacity: 0.9 },
                    },
                  },
                }}
              >
                <ToggleButton value={LEAVE_TYPE.FULL_DAY}>
                  Full Day
                </ToggleButton>
                <ToggleButton value={LEAVE_TYPE.FIRST_HALF}>
                  <WbSunnyOutlinedIcon className="leave-type-btn-icon" />
                  First Half
                </ToggleButton>
                <ToggleButton value={LEAVE_TYPE.SECOND_HALF}>
                  <WbSunnyOutlinedIcon className="leave-type-btn-icon leave-type-btn-icon--flip" />
                  Second Half
                </ToggleButton>
              </ToggleButtonGroup>

              {leaveInfo && leaveInfo.days > 0 && (
                <div className="leave-day-badge-row">
                  <Chip
                    size="small"
                    icon={
                      leaveInfo.isSandwich
                        ? <WarningAmberIcon className="leave-chip-icon" />
                        : <InfoOutlinedIcon className="leave-chip-icon" />
                    }
                    label={badgeLabel(leaveInfo)}
                    className={`leave-day-chip ${leaveInfo.isSandwich ? 'leave-day-chip--sandwich' : 'leave-day-chip--normal'}`}
                  />
                </div>
              )}
            </Box>
          </Box>

          {/* Date row */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
            <HolidayDatePicker
              id="edit-leave-start-date"
              label="Start Date"
              value={startDate}
              onChange={(val) => {
                setStartDate(val);
                if (!halfDay && endDate && val > endDate) setEndDate('');
              }}
              min={minStartDate}
              max={maxEndDate}
              holidays={holidays}
              appliedLeaves={appliedLeaves}
              required
            />
            <HolidayDatePicker
              id="edit-leave-end-date"
              label="End Date"
              value={effectiveEndDate}
              onChange={(val) => {
                setEndDate(val);
              }}
              min={startDate || minStartDate}
              max={maxEndDate}
              holidays={holidays}
              appliedLeaves={appliedLeaves}
              required={!halfDay}
              disabled={halfDay}
            />
          </Box>

          {/* Holiday notice if selected range falls on company holidays */}
          {holidaysInRange.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.25,
                borderRadius: '10px',
                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <CelebrationRoundedIcon sx={{ color: '#059669', fontSize: 20, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 600 }}>
                Includes Holiday:&nbsp;
                {holidaysInRange.map((h) => `${h.holidayDate?.split('T')[0].split('-').reverse().join('/')} (${h.name})`).join(', ')}
              </Typography>
            </Box>
          )}

          <TextField
            id="edit-leave-reason"
            label="Reason"
            multiline
            rows={4}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            fullWidth
            required
            placeholder="Describe the reason for your leave..."
            size="small"
          />
        </DialogContent>

        <DialogActions className="modern-dialog-actions">
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            disabled={submitting}
            sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleOutlineIcon fontSize="small" />
              )
            }
            sx={{
              bgcolor: 'var(--primary)',
              '&:hover': { bgcolor: 'var(--primary)', opacity: 0.88 },
            }}
          >
            {submitting ? 'Updating...' : 'Update Leave'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditLeaveDialog;
